import { useEffect, useState, useRef } from "react";
import { Loader2, Trophy, Medal, Crown, Star, Clover, Award, Share2, Download, Plus, ChevronUp } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import api from "@/lib/api";
import Feed from "@/pages/Feed";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PlayerModal from "@/components/PlayerModal";

const periods = [
  { key: "day", label: "Jour" },
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year", label: "Année" },
  { key: "all", label: "Historique" },
];

const podiumPeriods = [
  { key: "week", label: "Semaine" },
  { key: "month", label: "Mois" },
  { key: "year", label: "Année" },
];

function Podium({ board }) {
  if (!board || board.length === 0) {
    return <div className="py-14 text-center text-[#697C6F]">Aucun classement pour cette période.</div>;
  }
  const places = ["1ER", "2ÈME", "3ÈME"];
  return (
    <div className="grid grid-cols-3 gap-2 sm:gap-4 items-end p-4 sm:p-6 bg-gradient-to-b from-[#00E676]/10 to-transparent rounded-xl" data-testid="podium">
      {[1, 0, 2].map((idx) => {
        const p = board[idx];
        if (!p) return <div key={idx} />;
        const isFirst = idx === 0;
        const color = idx === 0 ? "#FFD700" : idx === 1 ? "#C0C0C0" : "#CD7F32";
        return (
          <div key={idx} className={`flex flex-col items-center ${isFirst ? "order-2" : idx === 1 ? "order-1" : "order-3"}`} data-testid={`podium-place-${idx + 1}`}>
            <Crown size={isFirst ? 30 : 20} style={{ color }} className={isFirst ? "mb-1" : "mb-1 opacity-80"} />
            {p.avatar ? (
              <img src={p.avatar} alt={p.name} className={`rounded-full object-cover ${isFirst ? "h-16 w-16" : "h-12 w-12"}`} style={{ border: `3px solid ${color}` }} />
            ) : (
              <div className={`grid place-items-center rounded-full font-black text-[#04180a] ${isFirst ? "h-16 w-16 text-2xl" : "h-12 w-12 text-lg"}`} style={{ background: color }}>
                {idx === 0 ? 1 : idx === 1 ? 2 : 3}
              </div>
            )}
            <div className={`mt-2 font-bold text-center truncate max-w-full px-1 ${isFirst ? "text-sm" : "text-xs"} ${p.is_me ? "text-[#39FF14]" : "text-white"}`}>{p.name}</div>
            <div className="tabular text-[#39FF14] font-black text-base" data-testid={`podium-points-${idx + 1}`}>{p.points} pts</div>
            <div
              className={`w-full rounded-t-lg mt-2 flex items-center justify-center font-black tracking-wide ${isFirst ? "h-24 text-base" : idx === 1 ? "h-16 text-sm" : "h-10 text-xs"}`}
              style={{ background: `linear-gradient(to top, ${color}55, ${color}22)`, border: `1px solid ${color}66`, color }}
            >
              {places[idx === 0 ? 0 : idx === 1 ? 1 : 2]}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SeasonsView({ seasons }) {
  const MEDALS = ["🥇", "🥈", "🥉", "🍫"];
  const PLACE = ["1er", "2e", "3e", "4e"];
  if (seasons === null) {
    return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>;
  }
  if (seasons.length === 0) {
    return <div className="py-16 text-center text-[#697C6F]">Aucune saison pour le moment.</div>;
  }
  return (
    <div className="space-y-4" data-testid="seasons-list">
      {seasons.map((s) => (
        <div key={s.ym} className="rounded-xl border border-[#00E676]/15 bg-[#131F17] overflow-hidden" data-testid={`season-${s.season}`}>
          <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-[#00E676]/10 to-transparent border-b border-[#00E676]/10">
            <div className="flex items-center gap-2">
              <span className="font-black text-lg">{s.label}</span>
              <span className="text-sm text-[#A1B5A8] capitalize">· {s.month_label}</span>
            </div>
            {s.is_current && <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#39FF14]/15 text-[#39FF14] animate-pulse">EN COURS</span>}
          </div>
          {s.podium.length === 0 ? (
            <div className="px-5 py-6 text-center text-sm text-[#697C6F]">Pas encore de classement pour cette saison.</div>
          ) : (
            <div className="divide-y divide-[#00E676]/5">
              {s.podium.map((p, i) => (
                <div key={i} className={`flex items-center gap-3 px-5 py-3 ${p.is_me ? "bg-[#00E676]/10" : ""}`}>
                  <span className="text-2xl w-8 text-center" aria-hidden>{MEDALS[i]}</span>
                  <span className="text-xs font-bold text-[#697C6F] w-8">{PLACE[i]}</span>
                  <span className={`font-semibold flex-1 truncate ${p.is_me ? "text-[#39FF14]" : ""}`}>
                    {p.name} {p.is_me && <span className="text-[10px] text-[#39FF14]">(vous)</span>}
                  </span>
                  <span className="tabular font-black text-[#39FF14]">{p.points} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function Challenge() {
  const [period, setPeriod] = useState("week");
  const [board, setBoard] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [histCount, setHistCount] = useState(10);
  const [showAllBoard, setShowAllBoard] = useState(false);

  const [showPodium, setShowPodium] = useState(false);
  const [podiumPeriod, setPodiumPeriod] = useState("week");
  const [podiumBoard, setPodiumBoard] = useState([]);
  const [podiumLoading, setPodiumLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const shareRef = useRef(null);
  const [seasons, setSeasons] = useState(null);

  useEffect(() => {
    api.get("/challenge/me").then((r) => setMe(r.data));
  }, []);

  useEffect(() => {
    if (period !== "all") return;
    setSeasons(null);
    api.get("/challenge/seasons").then((r) => setSeasons(r.data)).catch(() => setSeasons([]));
  }, [period]);

  useEffect(() => {
    setLoading(true);
    setShowAllBoard(false);
    api.get("/challenge/leaderboard", { params: { period } }).then((r) => setBoard(r.data)).finally(() => setLoading(false));
  }, [period]);

  useEffect(() => {
    if (!showPodium) return;
    setPodiumLoading(true);
    api.get("/challenge/leaderboard", { params: { period: podiumPeriod } }).then((r) => setPodiumBoard(r.data)).finally(() => setPodiumLoading(false));
  }, [showPodium, podiumPeriod]);

  const rankColor = (r) => (r === 1 ? "text-[#FFD700]" : r === 2 ? "text-[#C0C0C0]" : r === 3 ? "text-[#CD7F32]" : "text-[#697C6F]");
  const myRow = board.find((r) => r.is_me);

  const periodLabel = podiumPeriods.find((p) => p.key === podiumPeriod)?.label || "";
  const shareText = `🏆 Podium ${periodLabel} — A Nous Turf 🍀 Rejoins le Classement des turfistes !`;

  const buildImage = async () => {
    if (!shareRef.current) return null;
    const dataUrl = await toPng(shareRef.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#0B140E" });
    return dataUrl;
  };

  const handleShare = async () => {
    if (podiumBoard.length === 0) {
      toast.error("Aucun podium à partager pour cette période.");
      return;
    }
    setSharing(true);
    try {
      const dataUrl = await buildImage();
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "podium-anousturf.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Podium A Nous Turf", text: shareText });
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = "podium-anousturf.png";
        a.click();
        toast.success("Image du podium téléchargée ! Vous pouvez la partager.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Impossible de générer l'image du podium.");
    } finally {
      setSharing(false);
    }
  };

  const handleDownload = async () => {
    if (podiumBoard.length === 0) {
      toast.error("Aucun podium à télécharger pour cette période.");
      return;
    }
    setSharing(true);
    try {
      const dataUrl = await buildImage();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "podium-anousturf.png";
      a.click();
      toast.success("Image du podium téléchargée !");
    } catch (e) {
      toast.error("Impossible de générer l'image du podium.");
    } finally {
      setSharing(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-[#39FF14] font-script text-2xl">La compétition des turfistes</div>
        <h1 className="text-4xl sm:text-5xl font-black flex items-center gap-3">
          Classement <Clover className="text-[#39FF14] fill-[#39FF14]" />
        </h1>
      </div>

      <div className="grid md:grid-cols-[1fr_340px] gap-6 items-start">
        <div>
      {me && (
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] p-5">
            <Medal className="text-[#00E676]" />
            <div className="tabular text-3xl font-black mt-2">{me.plays}</div>
            <div className="text-xs text-[#A1B5A8]">Paris joués</div>
          </div>
          <div className="rounded-xl border border-[#39FF14]/30 bg-gradient-to-br from-[#00E676]/10 to-transparent p-5">
            <Star className="text-[#39FF14]" />
            <div className="tabular text-3xl font-black mt-2">{me.total_points}</div>
            <div className="text-xs text-[#A1B5A8]">Mes points</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {periods.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`period-${p.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              period === p.key ? "bg-[#00E676] text-[#04180a]" : "bg-[#131F17] text-[#A1B5A8] hover:text-white"
            }`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setShowPodium(true)}
          data-testid="open-podium-btn"
          className="ml-auto flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold bg-gradient-to-r from-[#FFD700] to-[#CD7F32] text-[#04180a] hover:brightness-110 transition-all shadow-[0_0_18px_rgba(255,215,0,0.25)]"
        >
          <Award size={18} /> Podium
        </button>
      </div>

      {period === "all" ? (
        <SeasonsView seasons={seasons} />
      ) : (
      <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-[#00E676]" /></div>
        ) : board.length === 0 ? (
          <div className="py-16 text-center text-[#697C6F]">Aucun classement pour cette période.</div>
        ) : (
          <>
          <table className="w-full" data-testid="leaderboard">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#697C6F] border-b border-[#00E676]/10">
                <th className="px-5 py-3 w-16">#</th>
                <th className="px-5 py-3">Pronostiqueur</th>
                <th className="px-5 py-3 text-center">Paris</th>
                <th className="px-5 py-3 text-right">Points</th>
              </tr>
            </thead>
            <tbody>
              {(showAllBoard ? board : board.slice(0, 20)).map((row) => (
                <tr
                  key={row.user_id}
                  className={`border-b border-[#00E676]/5 ${row.is_me ? "bg-[#00E676]/10" : ""}`}
                >
                  <td className={`px-5 py-3 font-black text-lg ${rankColor(row.rank)}`}>
                    {row.rank <= 3 ? <Crown size={18} className="inline mb-1" /> : row.rank}
                  </td>
                  <td className="px-5 py-3 font-semibold">
                    <button onClick={() => setSelectedPlayer(row.user_id)} data-testid={`player-link-${row.user_id}`} className="hover:text-[#39FF14] hover:underline underline-offset-2 transition-colors text-left">
                      {row.name}
                    </button>
                    {row.is_me && <span className="text-[10px] text-[#39FF14] ml-1">(vous)</span>}
                  </td>
                  <td className="px-5 py-3 text-center tabular text-[#A1B5A8]">{row.plays}</td>
                  <td className="px-5 py-3 text-right tabular font-black text-[#39FF14]">{row.points}</td>
                </tr>
              ))}
              {!showAllBoard && myRow && myRow.rank > 20 && (
                <>
                  <tr data-testid="leaderboard-gap">
                    <td colSpan={4} className="px-5 py-1.5 text-center text-[#697C6F] tracking-widest">···</td>
                  </tr>
                  <tr className="bg-[#00E676]/10 border-t border-[#00E676]/20" data-testid="leaderboard-my-row">
                    <td className={`px-5 py-3 font-black text-lg ${rankColor(myRow.rank)}`}>{myRow.rank}</td>
                    <td className="px-5 py-3 font-semibold">
                      <button onClick={() => setSelectedPlayer(myRow.user_id)} className="hover:text-[#39FF14] hover:underline underline-offset-2 transition-colors text-left">
                        {myRow.name}
                      </button>
                      <span className="text-[10px] text-[#39FF14] ml-1">(vous)</span>
                    </td>
                    <td className="px-5 py-3 text-center tabular text-[#A1B5A8]">{myRow.plays}</td>
                    <td className="px-5 py-3 text-right tabular font-black text-[#39FF14]">{myRow.points}</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
          {board.length > 20 && (
            <button
              onClick={() => setShowAllBoard((v) => !v)}
              data-testid="leaderboard-toggle"
              className="w-full flex items-center justify-center gap-2 py-3 text-sm font-semibold text-[#39FF14] border-t border-[#00E676]/10 hover:bg-[#1A2B20] transition-colors"
            >
              {showAllBoard ? <><ChevronUp size={16} /> Réduire le classement</> : <><Plus size={16} /> Voir tout le classement ({board.length} joueurs)</>}
            </button>
          )}
          </>
        )}
      </div>
      )}

      {me?.history?.length > 0 && (
        <div className="mt-10">
          <h2 className="text-lg font-bold mb-3">Mon historique récent</h2>
          <div className="rounded-xl border border-[#00E676]/15 bg-[#131F17] divide-y divide-[#00E676]/5" data-testid="challenge-history">
            {me.history.slice(0, histCount).map((h, i) => (
              <div key={`${h.date}-${h.numero}-${i}`} className="flex items-center justify-between px-5 py-3 text-sm">
                <div>
                  <span className="text-[#697C6F]">{new Date(h.date).toLocaleDateString("fr-FR")}</span>{" "}
                  <span className="font-medium">{h.hippodrome} {h.course}</span>{" "}
                  <span className="text-[#A1B5A8]">— mon n°{h.numero}</span>
                </div>
                <span className={`tabular font-bold ${h.points > 0 ? "text-[#39FF14]" : "text-[#697C6F]"}`}>+{h.points} pts</span>
              </div>
            ))}
          </div>
          {histCount < me.history.length && (
            <button
              onClick={() => setHistCount((c) => c + 10)}
              data-testid="challenge-history-more"
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold text-[#39FF14] bg-[#131F17] border border-[#00E676]/20 hover:bg-[#1A2B20] transition-colors"
            >
              <Plus size={16} /> Voir 10 de plus ({me.history.length - histCount} restant{me.history.length - histCount > 1 ? "s" : ""})
            </button>
          )}
        </div>
      )}
        </div>
        <Feed />
      </div>

      <Dialog open={showPodium} onOpenChange={setShowPodium}>
        <DialogContent className="max-w-xl border-[#FFD700]/30 bg-[#0B140E] text-white" data-testid="podium-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl font-black">
              <Award className="text-[#FFD700]" /> Podium des turfistes
            </DialogTitle>
          </DialogHeader>

          <div className="flex gap-2">
            {podiumPeriods.map((p) => (
              <button
                key={p.key}
                onClick={() => setPodiumPeriod(p.key)}
                data-testid={`podium-period-${p.key}`}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  podiumPeriod === p.key ? "bg-[#FFD700] text-[#04180a]" : "bg-[#131F17] text-[#A1B5A8] hover:text-white"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="min-h-[240px] flex items-center justify-center">
            {podiumLoading ? (
              <Loader2 className="w-7 h-7 animate-spin text-[#FFD700]" />
            ) : (
              <div ref={shareRef} className="w-full rounded-xl bg-[#0B140E] p-4" data-testid="podium-capture">
                <div className="flex items-center justify-end mb-2 px-1">
                  <span className="text-xs font-semibold text-[#FFD700]">Podium {periodLabel}</span>
                </div>
                <Podium board={podiumBoard} />
              </div>
            )}
          </div>

          <div className="border-t border-[#00E676]/10 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <button
                onClick={handleShare}
                disabled={sharing || podiumBoard.length === 0}
                data-testid="share-podium-btn"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-gradient-to-r from-[#00E676] to-[#39FF14] text-[#04180a] hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {sharing ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />} Partager la photo du podium
              </button>
              <button
                onClick={handleDownload}
                disabled={sharing || podiumBoard.length === 0}
                data-testid="download-podium-btn"
                title="Télécharger l'image"
                className="grid place-items-center h-[42px] w-[42px] rounded-lg bg-[#131F17] text-[#A1B5A8] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Download size={18} />
              </button>
            </div>
            <p className="text-center text-xs text-[#697C6F]">
              La photo du podium est partagée directement sur vos réseaux (Snapchat, Messenger, WhatsApp…) ou téléchargée pour la publier en story.
            </p>
          </div>
        </DialogContent>
      </Dialog>
      <PlayerModal userId={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
    </div>
  );
}
